import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { logger } from '@/utils/logger';

export interface ResolvedCreateParams {
  planId: string | null;
  editPost: string | null;
  repurpose: string | null;
  referencePost: string | null;
  draftId: string | null;
  product: string | null;
  mode: string | null;
  prefill: string | null;
  products: boolean;
  newChat: boolean;
  autosubmit: boolean;
  intent: string | null;
  topic: string | null;
  inspiration: string | null;
  platforms: string[] | null;
  scheduleDate: string | null;
  sourceType: string | null;
  sourceId: string | null;
  /** Session to resume when arriving from the Documents tab's "Open in chat". */
  resumeSession: string | null;
  /** Target message to scroll into view + briefly highlight on arrival. */
  messageId: string | null;
  /** Direct video_jobs.id to resume on mount (optional — falls back to session's active_refs.lastVideoJobId). */
  resumeVideoJobId: string | null;
  /** Media asset id to pre-load into the next reference slot of the next generated video/image. */
  addReference: string | null;
  /** Explicit chat session id (PD-6 T3.1): library handoff navigates to
   * `/?session=<new-uuid>` to spawn a fresh chat session bound to that
   * id. Without this param the page falls back to the discover-session
   * sentinel (`getOrCreateDiscoverSessionId`) and the handoff consumer would
   * never match. UUID-validated. */
  session: string | null;
  /** Calendar slot id (PD-6 T3.3): home chat reads `?calendarSlot=<post-id>`
   * after a calendar-slot edit handoff and forwards `{ slotId, scheduledFor }`
   * into pageContext.calendarSlot for the agent. UUID-validated. */
  calendarSlot: string | null;
  /** Plan slot id (PD-6 T3.3): home chat reads `?slotId=<id>` paired with
   * `?planId=<id>` for plan-slot edits. UUID-validated. */
  slotId: string | null;
}

const uuidSchema = z.string().uuid();
const isoDateSchema = z.string().refine((v) => !Number.isNaN(Date.parse(v)), {
  message: 'Invalid ISO date',
});

function validateUuid(raw: string | null, key: string): string | null {
  if (!raw) return null;
  const result = uuidSchema.safeParse(raw);
  if (!result.success) {
    if (import.meta.env.DEV) {
      logger.warn(`[useResolveCreateParams] invalid UUID for ${key}: ${raw}`);
    }
    return null;
  }
  return result.data;
}

function validateString(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function validatePlatforms(raw: string | null): string[] | null {
  if (!raw) return null;
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && /^[a-z0-9-]+$/i.test(p));
  return parts.length > 0 ? parts : null;
}

function validateIsoDate(raw: string | null, key: string): string | null {
  if (!raw) return null;
  const result = isoDateSchema.safeParse(raw);
  if (!result.success) {
    if (import.meta.env.DEV) {
      logger.warn(`[useResolveCreateParams] invalid ISO date for ${key}: ${raw}`);
    }
    return null;
  }
  return result.data;
}

/**
 * Reads URL search params once at mount (memoized on location.key), validates
 * UUID params with Zod, strips invalid values. Does NOT strip the URL — callers
 * (e.g., CreateChatPage) decide when to canonicalize.
 */
export function useResolveCreateParams(): ResolvedCreateParams {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo<ResolvedCreateParams>(() => {
    return {
      planId: validateUuid(searchParams.get('planId'), 'planId'),
      editPost: validateUuid(searchParams.get('editPost'), 'editPost'),
      repurpose: validateUuid(searchParams.get('repurpose'), 'repurpose'),
      referencePost: validateUuid(searchParams.get('referencePost'), 'referencePost'),
      draftId: validateUuid(searchParams.get('draftId'), 'draftId'),
      product: validateUuid(searchParams.get('product'), 'product'),
      mode: validateString(searchParams.get('mode')),
      prefill: validateString(searchParams.get('prefill')),
      products: searchParams.get('products') === 'true',
      newChat: searchParams.get('new') === 'true',
      autosubmit: searchParams.get('autosubmit') === '1',
      intent: validateString(searchParams.get('intent')),
      topic: validateString(searchParams.get('topic')),
      inspiration: validateString(searchParams.get('inspiration')),
      platforms: validatePlatforms(searchParams.get('platforms')),
      scheduleDate: validateIsoDate(searchParams.get('scheduleDate'), 'scheduleDate'),
      sourceType: validateString(searchParams.get('sourceType')),
      sourceId: validateUuid(searchParams.get('sourceId'), 'sourceId'),
      resumeSession: validateUuid(searchParams.get('resumeSession'), 'resumeSession'),
      messageId: validateUuid(searchParams.get('messageId'), 'messageId'),
      resumeVideoJobId: validateUuid(searchParams.get('resumeVideoJobId'), 'resumeVideoJobId'),
      addReference: validateUuid(searchParams.get('addReference'), 'addReference'),
      session: validateUuid(searchParams.get('session'), 'session'),
      calendarSlot: validateUuid(searchParams.get('calendarSlot'), 'calendarSlot'),
      slotId: validateUuid(searchParams.get('slotId'), 'slotId'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}
